import { Stack } from '@mui/material';
import { PerUnitProduct } from '@vy/services';
import { DEFAULT_CURRENCY, FixedProduct, UsageProduct } from '@vy/types';
import { isNotNullOrUndefined, validateNotNullOrUndefined, withDefault } from '@vy/utils';
import { Suspense, useCallback, useState } from 'react';
import { withGlobalSearchParams } from '../../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';
import { usePlanById } from '../../../../hooks/requests/use-plan-by-id';
import { usePlanOverview } from '../../../../hooks/requests/use-plan-overview';
import { EditProductErpSettingsDialog } from '../../../contract/contract-page-components/ContractFixedFees/components/EditProductErpSettingsDialog';
import { ContractFixedFees } from '../../../contract/contract-page-components/ContractFixedFees/ContractFixedFees';
import { ContractPageFallbackComponent } from '../../../contract/contract-page-components/ContractPageFallbackComponent';
import { ContractSummaryDetails } from '../../../contract/contract-page-components/ContractSummaryDetails/ContractSummaryDetails';
import { ContractUsageProductsSection } from '../../../contract/contract-page-components/ContractUsageProducts/ContractUsageProductsSection';
import { PlanPageHeader } from './PlanHeader';
import { PlanViewLoader } from './PlanViewLoader';

type PlanContentProps = {
  planId: string;
};

const PlanContent: React.FC<PlanContentProps> = ({ planId }) => {
  const [erpDialogState, setErpDialogState] = useState<{
    product: FixedProduct | PerUnitProduct | UsageProduct | null;
    productName: string | null;
  }>({
    product: null,
    productName: null,
  });

  const handleOpenErpDialog = useCallback(
    (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => {
      setErpDialogState({ product, productName });
    },
    [],
  );

  const handleCloseErpDialog = useCallback(() => {
    setErpDialogState({ product: null, productName: null });
  }, []);

  const { data: planOverview, isLoading: isLoadingPlanOverview } = usePlanOverview(planId);
  const {
    data: { plan },
  } = usePlanById(planId);

  if (isLoadingPlanOverview) {
    return <PlanViewLoader />;
  }

  const currency = DEFAULT_CURRENCY;
  validateNotNullOrUndefined(planOverview);

  return (
    <Suspense fallback={<PlanViewLoader />}>
      <Stack sx={{ gap: 2 }}>
        <PlanPageHeader plan={plan} planOverview={planOverview.plan} />

        <ContractSummaryDetails
          plan={plan}
          currency={currency}
          refNumber={plan.id}
          revenue={planOverview.plan.revenue}
          customersCount={planOverview.plan.customersCount}
          isPlan={true}
        />

        <ContractFixedFees plan={plan} currency={currency} onEditErp={handleOpenErpDialog} />

        <div className="mt-4">
          <ContractUsageProductsSection plan={plan} currency={currency} onEditErp={handleOpenErpDialog} />
        </div>
      </Stack>
      {isNotNullOrUndefined(erpDialogState.product) && (
        <EditProductErpSettingsDialog
          open={isNotNullOrUndefined(erpDialogState.product)}
          onClose={handleCloseErpDialog}
          productName={withDefault(erpDialogState.productName, '')}
          product={erpDialogState.product}
          contractId={planId}
        />
      )}
    </Suspense>
  );
};

const PlanContentWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(PlanContent), {
  errorBoundaryProps: {
    fallback: <ContractPageFallbackComponent text="Plan Not Found" />,
  },
});

export { PlanContentWithSuspenseBoundary as PlanContent };
